/**
 * @file WallboardErrorBoundary.js
 * @description Self-healing error boundary for the TV wallboard route.
 *
 * Without a boundary, any error thrown while mounting the wall — most often a
 * ChunkLoadError when the periodic reload lands mid-deploy and the new lazy
 * chunk 404s at the CDN edge — makes React 18 unmount the whole root. The TV
 * then shows an empty body until somebody presses F5. This boundary keeps the
 * screen dark, says what happened in small type, and reloads after a backoff
 * (see wallboardRecovery.js) so a broken build cannot thrash the display.
 *
 * Deliberately not the staff ErrorBoundary: that one is a Polaris page with a
 * "Try again" button, which is useless on a screen with no keyboard.
 */

import React from 'react';
import { recordCrashAndGetDelay } from './wallboardRecovery';
import './pairing.css';

const GROUND = { position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: '#04060c' };

export default class WallboardErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, delayMs: 0, attempt: 0 };
        this.timer = null;
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error('[wallboard] crashed; scheduling reload', error, info?.componentStack);
        const { delayMs, attempt } = recordCrashAndGetDelay();
        this.setState({ delayMs, attempt });
        this.timer = setTimeout(() => window.location.reload(), delayMs);
    }

    componentWillUnmount() {
        clearTimeout(this.timer);
    }

    render() {
        const { error, delayMs, attempt } = this.state;
        if (!error) return this.props.children;
        const seconds = Math.round(delayMs / 1000);
        const isChunk = /chunk|import|dynamically imported module/i.test(String(error?.name || '') + String(error?.message || ''));
        return (
            <div style={GROUND}>
                <div className="pwtv-fatal">
                    <div className="pwtv-fatal-title">RELOADING</div>
                    <div className="pwtv-fatal-msg">
                        {isChunk
                            ? 'A new version was deploying while this screen loaded.'
                            : 'The wallboard hit an error.'}
                        {' '}Reloading in {seconds}s{attempt > 1 ? ` (attempt ${attempt})` : ''}.
                    </div>
                    <div className="pwtv-fatal-sub">{String(error?.message || error).slice(0, 200)}</div>
                </div>
            </div>
        );
    }
}
